import React from 'react';
import {
  createHarmonyAndTransport,
  HarmonyEvent,
} from '@spotify-internal/harmony';
import base62Tohex from './base62-to-hex';
import decorate, { fetchUserByUsername } from './decorate';
import fetchColors from './fetch-colors';
import getTypeFromUri from './get-type-from-uri';
import spfetch from './spfetch';
import produce, { enableMapSet } from 'immer';

import algoPoolRegexp from './algo-pool-regexp';
import store from './store';
import dequal from 'dequal';
import getImagesUrlBySize from './get-images-url-by-size';
import getHubItemUri from './get-hub-item-uri';
import chroma from 'chroma-js';
import truncate from './truncate';

enableMapSet();

const API = 'https://api.spotify.com/v1/';

global.fetchColor = fetchColors;
global.getTypeFromUri = getTypeFromUri;

export const getPlaylistAndFilterKey = (playlistId, filter) =>
  JSON.stringify([playlistId, filter]);

const addedStateQueueByType = {
  track: [],
  album: [],
  artist: [],
  user: [],
};

const BUDDY_ENDPOINT = 'https://spclient.wg.spotify.com/presence-view/v1';

async function setupUser() {
  const { user } = store.getState();
  if (user) return { user: await user };
  const userPromise = spfetch(`${API}me`);
  const userFriends = spfetch(BUDDY_ENDPOINT);
  userFriends.then(d => {
    console.log(d);
  });
  store.setState({ user: userPromise });
  return userPromise.then((user) => {
    store.setState({ user });
    return { user };
  });
}

async function generatePlaylist(
  playlistId,
  newControlsByGroup,
  forceRefresh,
  sort
) {
  await setupUser();

  const isAlgoPool = algoPoolRegexp.test(playlistId);

  const playlist = store.getState()?.playlistsById?.[playlistId];
  const isAutoRefreshEnabled = playlist?.isAutoRefreshEnabled;

  const host = getHost();
  const urlObject = new URL(`${host}/p2-playground/v1/generate`);
  if (!host.startsWith('https://playlist-playground.spotify'))
    urlObject.searchParams.set('username', store.getState().user.id);

  urlObject.searchParams.set(
    'playlist-info',
    'filtered_genre_cluster,filtered_mood_cluster'
  );

  urlObject.searchParams.set(
    'objective',
    sort
      ? `sort:param=${sort.name},dir=${sort.asc ? 'asc' : 'desc'}`
      : `sort:param=${isAlgoPool ? 'w2v_distance' : 'pos_in_source'},dir=asc`
  );

  urlObject.searchParams.set(
    'pool-info',
    'filtered_genre_cluster,filtered_mood_cluster'
  );

  urlObject.searchParams.set(
    'track-info',
    `small${sort ? `,${sort.name}` : ''}`
  );
  urlObject.searchParams.set('size', '100');
  urlObject.searchParams.set('controls', 'true');

  let steer;
  let changedControls = [];
  if (newControlsByGroup) {
    for (const [group, controls] of Object.entries(newControlsByGroup)) {
      const controlsArray = Object.values(controls);
      controlsArray
        .filter(({ value, origValue }) => value !== origValue)
        .forEach(({ name, term, mid, max, value }) =>
          changedControls.push(
            `${group}${`${group !== term ? `:${term}` : ''}`}:${
              controlsArray.length === 1
                ? value
                : value === 2
                ? max
                : value === 0
                ? 0
                : mid
            }`
          )
        );
    }
    if (changedControls.length) {
      steer = changedControls.join(',');
      urlObject.searchParams.set('steer', steer);
    }
  }

  const ids = (playlist?.rowsBySteering?.[playlist?.steer] || [])
    .slice(0, 40)
    .map((row) => row?.track?.uri.substr(-22));

  const explicitlyAddedIds = (playlist?.explicitlyAddedSongs || [])
    .slice(0, 40)
    .map((row) => row?.track?.uri.substr(-22));

  urlObject.searchParams.set(
    'source',

    [
      `${isAlgoPool ? 'algo-pool' : 'playlist'}:pid=${playlistId},name=pl`,
      steer && `tracks:track-ids=${ids.join('|')},name=songs`,
      isAutoRefreshEnabled &&
        `tracks-similar-to-item:track-ids=${explicitlyAddedIds.join(
          '|'
        )},name=recs`,
      isAutoRefreshEnabled &&
        !explicitlyAddedIds.length &&
        playlist?.name &&
        `plex:term=${playlist.name}`,
      explicitlyAddedIds.length &&
        `tracks:track-ids=${explicitlyAddedIds.join('|')},name=explicit-songs`,
    ]
      .filter(Boolean)
      .flat()
      .join(';')
  );

  if (!changedControls.length && !isAlgoPool)
    urlObject.searchParams.set('base-rules', 'none');

  if (store.getState()?.playlistsById?.[playlistId]?.isAutoRefreshEnabled)
    steer += 'auto-refresh';

  if (sort) {
    steer += `&sort=${sort.name},dir=${sort.asc ? 'asc' : 'desc'}`;
  }

  if (
    steer in
      (store.getState()?.playlistsById?.[playlistId]?.rowsBySteering || {}) &&
    !forceRefresh
  )
    return {
      steer,
      rows: store.getState()?.playlistsById?.[playlistId]?.rowsBySteering[
        steer
      ],
      controlsByGroup: newControlsByGroup,
    };

  return spfetch(urlObject.toString()).then(
    (body) => {
      const rows = body?.playlist?.map((data) => {
        const {
          album_image_large,
          album_image_small,
          album_name,
          album_uri,
          name,
          primary_artist_name,
          primary_artist_uri,
          uri,
        } = data;

        let sortValue;
        const rawSortValue = data[sort?.name];
        switch (sort?.name) {
          case 'tempo':
            sortValue = Math.round(rawSortValue);
            break;

          case 'duration_ms':
            sortValue = formatTime(rawSortValue);
            break;

          case 'ts_added':
            sortValue =
              rawSortValue &&
              new Date(rawSortValue * 1000).toISOString().substr(0, 10);
            break;

          case 'release_date':
          case 'popularity':
          case 'track_affinity':
            sortValue = rawSortValue;
            break;

          case 'album_name':
            sortValue = rawSortValue?.[0];
            break;

          case 'energy':
          case 'acousticness':
          case 'danceability':
            sortValue = Math.round(rawSortValue * 100);
            break;

          default:
            sortValue = null;
        }

        return {
          uid: uri.substr(-22),
          track: {
            uri,
            name,
            album: {
              uri: album_uri,
              name: album_name,
              images: [album_image_large, album_image_small].map((url) => ({
                url,
              })),
            },
            artists: [
              {
                uri: primary_artist_uri,
                name: primary_artist_name,
              },
            ],
            sortValue,
          },
        };
      });

      store.setState(
        produce(
          store.getState(),
          (draft) =>
            rows &&
            rows.forEach(
              (row) => (draft.tracksByUri[row?.track?.uri] = row?.track)
            )
        )
      );

      const controlsByGroup = produce(
        store.getState()?.playlistsById?.[playlistId]?.controlsByGroup || {
          'auto-refresh': {
            'auto-refresh': {
              value: false,
              origValue: false,
            },
          },
        },
        (controlsByGroup) =>
          body?.controls?.reduce((controlsByGroup, control) => {
            if (!(control.name in controlsByGroup)) {
              controlsByGroup[control.name] = {};
            }

            const { min, cur, max } = control;
            const mid = (min + max) / 2;
            const value = cur > mid ? 2 : cur === 0 ? 0 : 1;
            if (!control.term) control.term = control.name; // FIXME:

            if (!(control.term in controlsByGroup[control.name])) {
              controlsByGroup[control.name][control.term] = {
                ...control,
                orig: control.cur,
                mid,
                value,
                origValue: value,
              };
            } else {
              controlsByGroup[control.name][control.term] = {
                value,
                ...controlsByGroup[control.name][control.term],
                ...control,
              };
            }

            return controlsByGroup;
          }, controlsByGroup)
      );

      return {
        steer,
        rows,
        sort,

        controlsByGroup,
        ...[
          { keyIn: 'playlist_info', keyOut: 'playlistInfo' },
          { keyIn: 'pool_info', keyOut: 'poolInfo' },
        ]
          .map((info) => ({
            [info.keyOut]: {
              ...[
                {
                  keyIn: 'filtered_genre_cluster',
                  keyOut: 'filteredGenres',
                },
                { keyIn: 'filtered_mood_cluster', keyOut: 'filteredMoods' },
              ]
                .map((field) => ({
                  [field.keyOut]: Object.entries(
                    body?.[info.keyIn]?.[field.keyIn]?.counts || {}
                  )
                    .sort((a, b) => b[1] - a[1])
                    .map(([name]) => name)
                    .filter((name) => name !== '(unknown)'),
                }))
                .reduce((map, o) => Object.assign(map, o), {}),
            },
          }))
          .reduce((map, o) => Object.assign(map, o), {}),
      };
    },
    (error) => {
      console.error(error);
    }
  );
}

const actions = (store) => ({
  async fetchPlaylistRadioTags(state, playlistId) {
    const host = getHost();
    const urlObject = new URL(`${host}/p2-playground/v1/generate`);
    if (!host.startsWith('https://playlist-playground.spotify'))
      urlObject.searchParams.set('username', store.getState().user.id);

    urlObject.searchParams.set('controls', 'true');

    urlObject.searchParams.set('track-info', `micro`);
    urlObject.searchParams.set('source', `playlist:pid=${playlistId}`);

    return spfetch(urlObject.toString()).then((body) => {
      return produce(store.getState(), (draft) => {
        if (!draft.playlistsById[playlistId])
          draft.playlistsById[playlistId] = {};
        draft.playlistsById[playlistId].tags = body?.controls
          ?.filter(
            (control) => control?.type === 'term' && control?.name === 'genre'
          )
          .map((control) => control?.term);
      });
    });
  },

  async fetchPlaylistTagRadio(
    { playlistsById, stationsByIdAndSteering },
    playlistId,
    genre,
    forceRefresh
  ) {
    if (stationsByIdAndSteering[`${playlistId}${genre}`]?.rows && !forceRefresh)
      return;

    const host = getHost();
    const urlObject = new URL(`${host}/p2-playground/v1/generate`);
    if (!host.startsWith('https://playlist-playground.spotify'))
      urlObject.searchParams.set('username', store.getState().user.id);

    if (!playlistsById[playlistId]?.rows) await this.fetchPlaylist(playlistId);

    const trackIds = store
      .getState()
      .playlistsById[playlistId]?.rows?.filter(
        (row) => getTypeFromUri(row?.track?.uri) === 'track'
      )
      ?.map((row) => row?.track?.uri?.substr(-22));

    urlObject.searchParams.set('track-info', `small`);
    urlObject.searchParams.set(
      'source',
      [
        `tracks-similar-to-item:track-ids=${trackIds.slice(0, 100).join('|')}`,
        `tracks:track-ids=${trackIds
          .filter(() => Math.random() < 0.5)
          .slice(0, 50)
          .join('|')},name=explicit-songs`,
      ].join(';')
    );
    urlObject.searchParams.set('steer', `genre:${genre}:100.000`);
    urlObject.searchParams.set('sort', 'param=w2v_distance,dir=asc');

    return spfetch(urlObject.toString()).then(async (body) => {
      const uniqueTrackAlbumImageUrlsSet = new Set(
        body?.playlist?.map((row) => row.album_image_large)?.filter(Boolean) ||
          []
      );

      const imageUrl =
        uniqueTrackAlbumImageUrlsSet.size >= 4
          ? `https://mosaic.scdn.co/300/${Array.from(
              uniqueTrackAlbumImageUrlsSet
            )
              .slice(0, 4)
              .map((uniqueTrackAlbumImageUrl) =>
                uniqueTrackAlbumImageUrl.substr(-40)
              )
              .join('')}`
          : Array.from(uniqueTrackAlbumImageUrlsSet)?.[0] || null;

      const color = (await fetchColors(imageUrl))?.VIBRANT;

      return produce(store.getState(), (draft) => {
        draft.stationsByIdAndSteering[`${playlistId}${genre}`] = {
          name: `${genre} Radio`,
          color,
          imageUrl,
          isLoaded: true,
          rows:
            body.playlist?.map(
              ({
                album_image_large,
                album_image_small,
                album_name,
                album_uri,
                name,
                primary_artist_name,
                primary_artist_uri,
                uri,
                source,
              }) => ({
                uid: uri.substr(-22),
                metadata: { source },
                track: {
                  uri,
                  name,
                  album: {
                    uri: album_uri,
                    name: album_name,
                    images: [album_image_large, album_image_small].map(
                      (url) => ({
                        url,
                      })
                    ),
                  },
                  artists: [
                    {
                      uri: primary_artist_uri,
                      name: primary_artist_name,
                    },
                  ],
                },
              })
            ) || [],
        };
      });
    });
  },

  async fetchPlaylist(state, playlistId, forceRefresh, sort) {
    if (
      playlistId?.length !== 22 ||
      (state.playlistsById[playlistId]?.rows && !forceRefresh)
    )
      return;

    try {
      const metadataPromise = spfetch(
        `https://api.spotify.com/v1/playlists/${playlistId}`
      ).then(
        // FIXME: fields
        async ({
          name,
          description,
          owner: { display_name: ownerDisplayName, id: ownerId } = {},
          followers: { total: followersTotal } = {},
          images: [{ url: imageUrl } = {}] = [],
          tracks: { items: fallbackRows = [] } = {},
        }) => {
          const ownerImageUrl =
            ownerId && (await fetchUserByUsername(ownerId))?.imageUrl;

          return {
            name,
            description,
            ownerDisplayName,
            ownerImageUrl,
            followersTotal,
            imageUrl,
            ownerId,
            isOwnedByCurrentUser: ownerId === store.getState().user.id,
            isOwnedBySpotify: ownerId === 'spotify', //FIXME
            fallbackRows,
          };
        }
      );

      const colorPromise = fetchColors(`spotify:playlist:${playlistId}`);

      const followPromise = spfetch(
        `https://api.spotify.com/v1/playlists/${playlistId}/followers/contains?ids=${encodeURIComponent(
          store.getState().user.id
        )}`
      );

      const generatePromise =
        forceRefresh && sort
          ? generatePlaylist(
              playlistId,
              state.playlistsById[playlistId]?.controlsByGroup,
              forceRefresh,
              sort
            )
          : {};

      this.fetchPlaylistRadioTags(playlistId);

      const [
        metadataResponse,
        colorResponse,
        followResponse,
        generateResponse,
      ] = await Promise.all([
        metadataPromise,
        colorPromise,
        followPromise,
        generatePromise,
      ]);

      const {
        name,
        description,
        followersTotal,
        ownerDisplayName,
        ownerImageUrl,
        imageUrl,
        ownerId,
        isOwnedByCurrentUser,
        isOwnedBySpotify,
        fallbackRows,
      } = metadataResponse || {};
      const colors = colorResponse;
      const isFollowing = !!followResponse?.[0];

      const {
        rows: generatedRows = fallbackRows,
        playlistInfo,
        poolInfo,
        controlsByGroup,
        steer,
      } = generateResponse || {};

      const rows = generatedRows.filter((row) => row.track);

      rows.forEach((row) => {
        if (!row.uid) row.uid = row?.track?.uri;
      });

      return produce(store.getState(), (draft) => {
        draft.playlistsById[playlistId] = {
          explicitlyAddedSongs: rows,
          // Put before splat to be able to pre-set when dynamic playlist
          name,
          ownerDisplayName,
          ownerImageUrl,
          isOwnedByCurrentUser,
          isOwnedBySpotify,

          ...draft.playlistsById[playlistId],
          id: playlistId,
          description,
          followersTotal,
          color: colors?.VIBRANT && chroma(colors.VIBRANT).luminance(0.2),
          imageUrl,
          isFollowing,

          rows,
          playlistInfo,
          poolInfo,
          controlsByGroup,
          rowsBySteering: {
            ...draft.playlistsById[playlistId]?.rowsBySteering,
            [steer]: rows,
          },
          steer,
          pinnedSet: new Set(),
          sort,
        };
      });
    } catch (error) {
      console.error(error);
      return produce(store.getState(), (draft) => {
        draft.playlistsById[playlistId] = { error };
      });
    }
  },

  fetchPlaylistFormat(state, playlistId) {
    if (state.playlistsById[playlistId]?.format) return;

    return spfetch(
      `https://spclient.wg.spotify.com/playlist/v2/playlist/${playlistId}/metadata?format=json`
    ).then((data) =>
      produce(store.getState(), (draft) => {
        if (!draft.playlistsById[playlistId])
          draft.playlistsById[playlistId] = {};

        draft.playlistsById[playlistId].format = data.attributes.format;
      })
    );
  },

  async sendAdaptiveRadioSignal(
    { harmony, tracksByUri, adaptiveSignalByContextUriAndTrackUri },
    signal,
    contextUri,
    trackUri
  ) {
    const currentSignal =
      adaptiveSignalByContextUriAndTrackUri?.[contextUri]?.[trackUri];
    if (signal === 'like' || signal === 'dislike') {
      if (currentSignal !== signal) {
        this.showNotification(
          signal === 'like'
            ? `Adding more like this to your Radio sessions.`
            : `Removing tracks like ${truncate(
                tracksByUri[trackUri]?.name,
                24
              )}...`,
          Date.now(),
          <g pointerEvents="bounding-box">
            <rect
              width="60"
              height="44"
              fill="#fff"
              y="-24"
              x="-44"
              fill-opacity="0.001"
            ></rect>
            <text
              fill="#1DB954"
              style={{ whiteSpace: 'pre' }}
              fontSize="10"
              fontWeight="bold"
              letterSpacing=".1em"
              textAnchor="end"
            >
              <tspan>UNDO</tspan>
            </text>
          </g>
        );
      }

      store.setState(
        produce(store.getState(), (draft) => {
          if (!(contextUri in draft.adaptiveSignalByContextUriAndTrackUri))
            draft.adaptiveSignalByContextUriAndTrackUri[contextUri] = {};
          draft.adaptiveSignalByContextUriAndTrackUri[contextUri][trackUri] =
            signal === currentSignal ? null : signal;
        })
      );
    }

    await spfetch(
      `https://spclient.wg.spotify.com/adaptive-radio-signals/v1/send-signal/${encodeURIComponent(
        signal
      )}/${encodeURIComponent(contextUri)}/${encodeURIComponent(
        trackUri
      )}?deleted=${signal === currentSignal}`,
      { method: 'POST' }
    );
    if (getTypeFromUri(contextUri) === 'playlist')
      await this.fetchPlaylist(contextUri.substr(-22), true);

    if (signal !== currentSignal && signal === 'dislike')
      await harmony.nextTrack();
  },

  setActiveTabIndex(state, activeTabIndex) {
    return { activeTabIndex };
  },

  async login() {
    return { isLoggedIn: await spfetch.login() };
  },

  async loginWithRefreshTokenIfAvailable() {
    return { isLoggedIn: await spfetch.loginWithRefreshTokenIfAvailable() };
  },

  async logout() {
    spfetch.logout();
    return { isLoggedIn: false };
  },

  setupUser,

  setupHarmony() {
    const { harmony, transport } = createHarmonyAndTransport({
      getToken: async (callback) =>
        callback((await spfetch('/v1/me')) && spfetch.getToken()),
      controller: {
        enableWithoutStreamer: true,
        decorateTrackWindow: true,
      },
      streamer: {
        cubicVolume: true,
      },
      claimInactivePlayerStates: true,
    });

    global.harmony = harmony;

    harmony.on(
      HarmonyEvent.STATE_CHANGED,
      async ({ data: { state: playerState } } = {}) => {
        const contextPlayerState =
          (await harmony.getContextPlayerState()) || undefined;

        const device = (await harmony.getLastActiveDevice()) || undefined;
        store.setState({
          playerState: playerState || undefined,
          device,
          devices: await harmony.getDevices(),
          contextPlayerState,
          descriptor: await harmony.getClientDescriptor(),
          ...(playerState &&
            !playerState.paused && {
              preview: {
                ...store.getState().preview,
                isPlaying: false,
              },
            }),
        });

        const {
          prev_tracks: previousRawRows = [],
          track: currentRawRow,
          next_tracks: nextRawRows = [],
          context_uri: contextUri,
          track: { uid: currentRowUid } = {},
        } = contextPlayerState || {};

        const trackIndicesByUri = {};
        [...previousRawRows, currentRawRow, ...nextRawRows]
          .filter(Boolean)
          .forEach((row) => {
            if (!row.uid) {
              row.uid = `${row.uri}:${(trackIndicesByUri[row.uri] =
                (trackIndicesByUri[row.uri] || 0) + 1)}`;
            }
          });

        const metaOrDelimiterPredicate = ({ uri }) =>
          /^spotify:(meta:page|delimiter)/.test(uri);

        const previousRowsMetaOrDelimiterIndex = previousRawRows
          .slice()
          .reverse()
          .findIndex(metaOrDelimiterPredicate);

        const previousContextRows = previousRawRows.slice(
          -1 *
            (previousRowsMetaOrDelimiterIndex === -1
              ? previousRawRows.length
              : previousRowsMetaOrDelimiterIndex),
          previousRowsMetaOrDelimiterIndex === 0 ? 0 : undefined
        );
        const previousRowsPromises = previousContextRows.map(decorate);

        const currentRowPromise = currentRawRow && decorate(currentRawRow);
        const nextRowsMetaOrDelimiterIndex = nextRawRows.findIndex(
          metaOrDelimiterPredicate
        );
        const nextContextRows = nextRawRows.slice(
          0,
          nextRowsMetaOrDelimiterIndex === -1
            ? nextRawRows.length
            : nextRowsMetaOrDelimiterIndex
        );
        const nextRowsPromises = nextContextRows.map(decorate);

        const previousCurrentAndNextUris = [
          ...previousContextRows.slice(-1),
          currentRawRow,
          ...nextContextRows.slice(0, 1),
        ]
          .filter(Boolean)
          .map(({ uri }) => uri);
        const colorsPromises = previousCurrentAndNextUris.map(fetchColors);

        previousCurrentAndNextUris.map((uri) =>
          this.fetchSavedStateForUri(uri)
        );

        const previousRows = await Promise.all(previousRowsPromises);
        const currentRow = await currentRowPromise;
        const nextRows = await Promise.all(nextRowsPromises);
        const colors = await Promise.all(colorsPromises);
        const colorsByUri = colors.reduce((colorsByUri, colors, i) => {
          colorsByUri[previousCurrentAndNextUris[i]] = colors;
          return colorsByUri;
        }, {});

        const index =
          (contextPlayerState &&
            contextPlayerState.index &&
            contextPlayerState.index.track) ||
          0;
        previousRows.unshift.apply(
          previousRows,
          new Array(Math.max(0, index - previousRows.length))
        );

        const rows = [...previousRows, currentRow, ...nextRows];

        store.setState({
          previousRows,
          currentRow,
          currentlyPlayingIndex: previousRows.length,
          nextRows,
          rows,
          contextUri,
          currentRowUid,
          colorsByUri,
        });

        if (
          getTypeFromUri(contextUri) === 'playlist' &&
          contextUri.match(/[^:]+$/)?.[0]?.length === 32 &&
          nextRows.length === 0
        ) {
          this.fetchMoreDynamicPlaylist(contextUri.match(/[^:]+$/)?.[0]);
        }

        if (getTypeFromUri(contextUri) === 'playlist') {
          this.fetchPlaylistFormat(contextUri.substr(-22));
        }
      }
    );

    harmony.once(HarmonyEvent.STATE_CHANGED, async () => {
      store.setState({
        isShuffleEnabled: (await harmony.getCurrentState())?.shuffle,
      });
    });

    harmony.on(HarmonyEvent.AUTHENTICATED, async (ev) => {
      const productState = await harmony.getProductState();
      store.setState({
        onDemand: productState?.['on-demand'] === '1',
        isPremium: productState?.['product'] === 'premium',
      });
    });

    harmony.on(HarmonyEvent.PLAYER_INITIALIZATION_DONE, async (event) => {
      await harmony.getCurrentState();
      store.setState({
        isPlayerReady: true,
      });
    });

    function onQueueChanged(internalPlayerQueue, playerQueue) {
      Promise.all((playerQueue?.next_tracks || []).map(decorate)).then((rows) =>
        store.setState(
          produce(store.getState(), (draft) => {
            rows.forEach((row) => {
              draft.tracksByUri[row?.track?.uri] = row?.track;
            });
          })
        )
      );

      store.setState({
        internalPlayerQueue,
        playerQueue,
      });
    }

    harmony.once(
      HarmonyEvent.LAST_ACTIVE_DEVICE_INFO_CHANGED,
      async (event) => {
        harmony.setVolume(0.15);

        const queueManager = harmony.getQueueManager();
        const internalPlayerQueuePromise = queueManager.getInternalPlayerQueue();
        const playerQueuePromise = queueManager.getPlayerQueue();

        onQueueChanged(
          await internalPlayerQueuePromise,
          await playerQueuePromise
        );
      }
    );

    harmony.on(HarmonyEvent.PLAYER_INITIALIZATION_FAILED, () => {
      store.setState({
        isPlayerReady: false,
      });
    });

    let isEnqueingNextStationTrack = false;
    harmony.on(HarmonyEvent.PROGRESS, async (event) => {
      const { contextUri, playerState, contextPlayerState } = store.getState();
      const stationId = contextUri?.match(
        /^spotify:internal:station:([^:]+)$/
      )?.[1];
      if (!stationId) return;

      const position = event?.data?.position;
      const duration = playerState?.duration;

      if (
        position &&
        duration &&
        duration - position < 6000 &&
        !isEnqueingNextStationTrack &&
        contextPlayerState?.next_tracks?.findIndex((item) =>
          item?.uri?.startsWith('spotify:delimiter')
        ) === 0
      ) {
        isEnqueingNextStationTrack = true;
        // const track = await fetchFakeNextTrack();
        const track = await spplplfetch(
          `/p2-playground/v1/station/next-track?station-id=${stationId}`
        ).then(({ next_track: nextTrack }) => nextTrack);

        if (!track) {
          isEnqueingNextStationTrack = false;
          return console.error('Failed getting next-track :-(');
        }

        await harmony.setQueue(
          [{ ...track }], // FIXME: handle explicitly queued etc
          store
            .getState()
            ?.contextPlayerState?.prev_tracks?.filter((track) =>
              /^spotify:(?:track|episode):[0-9a-zA-Z]{22}$/.test(track.uri)
            ) || []
        );

        await harmony.getContextPlayerState();
        await harmony.getCurrentState();
        await new Promise((resolve) => setTimeout(resolve, 1000));

        isEnqueingNextStationTrack = false;
      }
    });

    harmony
      .getQueueManager()
      .on(
        HarmonyEvent.PLAYER_QUEUE_CHANGED,
        ({ data: { internalPlayerQueue, playerQueue } = {} }) => {
          onQueueChanged(internalPlayerQueue, playerQueue);
        }
      );

    transport.connect().then(() => transport.authenticate());
    return { harmony };
  },
  transferPlaybackToDevice: async ({ harmony }, toId) => harmony.transfer(toId),
  previousTrack: ({ harmony }) => harmony.previousTrack(),
  smartPreviousTrack: ({ harmony }) => harmony.smartPreviousTrack(),
  nextTrack: ({ harmony }) => harmony.nextTrack(),

  playTracksFromUri: async (
    { harmony, featureIdentifier },
    { rows, index, shuffle = false, contextUri }
  ) =>
    harmony.playTracks(
      rows.map(({ track }) => track.uri),
      null,
      {
        index,
        shuffle,
        contextURI: contextUri,
        playOrigin: { featureIdentifier },
        autoplayCandidate: false,
      }
    ),

  seekTo({ harmony }, position) {
    harmony.seek(position);
  },

  togglePlayback({ harmony, playerState = {} }, event) {
    if (playerState.paused) harmony.resume();
    else harmony.pause();
    return {
      playerState: {
        ...playerState,
        paused: !playerState.paused,
      },
    };
  },

  playPrevious({ harmony }) {
    harmony.previousTrack();
  },

  playNext({ harmony, contextUri }) {
    const stationId = contextUri?.match(
      /^spotify:internal:station:([^:]+)$/
    )?.[1];
    if (stationId) {
      this.playNextStationTrack(stationId, 'skip');
    } else harmony.nextTrack();
  },

  async fetchRootlist() {
    const playlists = [];

    let playlistsBatch;
    let next = 'https://api.spotify.com/v1/me/playlists?limit=50';
    do {
      ({ items: playlistsBatch, next } = await spfetch(next));
      playlists.push.apply(
        playlists,
        playlistsBatch.filter(
          (playlist) =>
            !/^spotify/.test(playlist?.owner?.id) &&
            /^(https:\/\/mosaic\.scdn\.co\/[^\/]+\/[^\/]+|https:\/\/i\.scdn\.co\/[^\/]+\/ab67706c0000bebb[^\/]*)$/.test(
              playlist?.images?.[0]?.url
            ) &&
            playlist?.tracks?.total >= 5
        )
      );
    } while (next && playlists.length < 5);

    return {
      playlists,
    };
  },

  fetchStation(state, stationId) {
    return spplplfetch(
      `/p2-playground/v1/station/info?station-id=${encodeURIComponent(
        stationId
      )}`
    ).then(({ info }) =>
      produce(store.getState(), (draft) => {
        draft.stationsById[stationId] = {
          ...draft.stationsById[stationId],
          ...info,
        };
        (info?.seeds || []).forEach(
          (item) =>
            (draft[`${getTypeFromUri(item?.uri)}sByUri`][item?.uri] = item)
        );
      })
    );
  },

  fetchStationFutureTracks(state, stationId) {
    return spplplfetch(
      `/p2-playground/v1/station/future-tracks?station-id=${encodeURIComponent(
        stationId
      )}`
    ).then(({ future_tracks }) =>
      produce(store.getState(), (draft) => {
        draft.stationsById[stationId].futureTracks = future_tracks || [];
      })
    );
  },

  setSavedStateForUri(
    { addedStateByUri, playlistsById },
    uri,
    added,
    contextUri
  ) {
    const type = getTypeFromUri(uri);
    store.setState({ addedStateByUri: { ...addedStateByUri, [uri]: added } });
    const [, id, playlistId] = uri.match(
      /^spotify:[^:]+:([^:]+)(?::playlist:([^:]+))?/
    );

    if (
      type === 'track' &&
      added &&
      contextUri &&
      getTypeFromUri(contextUri) === 'playlist' &&
      playlistsById[contextUri.substr(-22)] &&
      playlistsById[contextUri.substr(-22)].isOwnedByCurrentUser
    ) {
      this.fetchTrackRowRecommendations(uri, contextUri);
    }

    return spfetch(
      {
        track: `${API}me/tracks?ids=${id}`,
        album: `${API}me/albums?ids=${id}`,
        artist: `${API}me/following?type=artist&ids=${id}`,
        user: `${API}me/following?type=user&ids=${id}`,
        playlist: `${API}users/${id}/playlists/${playlistId}/followers`,
      }[type],
      { method: added ? 'PUT' : 'DELETE' }
    ).then(({ status }) => ({
      addedStateByUri: {
        ...store.getState().addedStateByUri,
        [uri]: status === 200 ? added : !added,
      },
      ...(type === 'track' && {
        [added ? 'lastAddedTrackUri' : 'lastRemovedTrackUri']: uri,
        [added ? 'lastRemovedTrackUri' : 'lastAddedTrackUri']:
          store.getState()[
            added ? 'lastRemovedTrackUri' : 'lastAddedTrackUri'
          ] === uri
            ? null
            : store.getState()[
                added ? 'lastRemovedTrackUri' : 'lastAddedTrackUri'
              ],
      }),
    }));
  },

  toggleSavedStateForUri({ addedStateByUri }, uri, contextUri) {
    return this.setSavedStateForUri(uri, !addedStateByUri[uri], contextUri);
  },

  fetchSavedStateForUri(
    { addedStateByUri, user: { id: currentUserId } = {} },
    uri
  ) {
    const type = getTypeFromUri(uri);
    switch (type) {
      case 'track':
      case 'album':
      case 'artist':
      case 'user': //FIXME: this probably doesn't work
        const addedStateQueue = addedStateQueueByType[type];
        if (
          !(uri in addedStateByUri) &&
          !addedStateQueue.includes(uri) &&
          addedStateQueue.push(uri) === 1
        ) {
          return new Promise((resolve) => {
            setTimeout(function takeBatch(resolved) {
              const uris = addedStateQueue.splice(0, 50);
              if (!uris.length) return;
              const ids = uris.map((uri) => uri.substr(-22));
              store.setState({
                addedStateByUri: {
                  ...store.getState().addedStateByUri,
                  ...uris.reduce(
                    (map, uri) => Object.assign(map, { [uri]: false }),
                    {}
                  ),
                },
              });
              spfetch(
                {
                  track: `${API}me/tracks/contains?ids=`,
                  album: `${API}me/albums/contains?ids=`,
                  artist: `${API}me/following/contains?type=artist&ids=`,
                  user: `${API}me/following/contains?type=user&ids=`,
                }[type] + ids
              ).then((containment) => {
                const result = {
                  addedStateByUri: {
                    ...store.getState().addedStateByUri,
                    ...uris.reduce(
                      (map, uri, i) => ((map[uri] = containment[i]), map),
                      {}
                    ),
                  },
                };
                if (resolved) store.setState(result);
                else resolve(result);
                takeBatch(true);
              });
            });
          });
        }
        break;

      case 'playlist':
        const [, playlistId] = uri.match(
          /^spotify(?::user:[^:]+)?:playlist:([^:]+)/
        );
        if (playlistId?.length == 32)
          return produce(store.getState(), (draft) => {
            draft.addedStateByUri[uri] = true;
          });

        return spfetch(
          `${API}playlists/${playlistId}/followers/contains?ids=${currentUserId}`
        ).then(([added]) => ({
          addedStateByUri: {
            ...store.getState().addedStateByUri,
            [uri]: added,
          },
        }));
        break;
    }
  },

  async fetchTrackRowRecommendations({ playlistsById }, uri, contextUri) {
    const { rows = [] } = playlistsById[contextUri.substr(-22)] || {};

    const [
      { name: artistName, imageUrl: artistImageUrl },
      tracks,
    ] = await Promise.all([
      spfetch(`https://api.spotify.com/v1/tracks/${uri.substr(-22)}`).then(
        ({ artists: [{ id } = {}] = [] }) =>
          spfetch(`https://api.spotify.com/v1/artists/${id}`).then(
            ({
              name,
              images: [
                { url: largeImageUrl } = {},
                { url: mediumImageUrl } = {},
                { url: smallImageUrl } = {},
              ] = [],
            }) => ({
              name,
              imageUrl: smallImageUrl || mediumImageUrl || largeImageUrl,
            })
          )
      ),

      spfetch(
        'https://spclient.wg.spotify.com/playlistextender/ft/v1/assist-curation',
        {
          method: 'POST',
          body: {
            trackSkipIDs: rows.map(({ track: { uri = '' } = {} }) =>
              uri.substr(-22)
            ),
            trackIDs: [uri.substr(-22)],
            numResults: 3,
            type: 'KPE',
          },
        }
      ).then(({ recommended_tracks: tracks }) => tracks),
    ]);

    return {
      trackRowRecommendationsByContextUri: {
        ...store.getState().trackRowRecommendationsByContextUri,
        [contextUri]: {
          uri,
          artistName,
          artistImageUrl,
          tracks,
        },
      },
    };
  },

  async fetchAssistedCuration({ playlistsById }, playlistId, title) {
    const playlist = playlistsById[playlistId] || {};
    const rows = playlist?.rowsBySteering?.[playlist?.steer] || [];

    spfetch(
      'https://spclient.wg.spotify.com/playlistextender/ft/v1/assist-curation',
      {
        method: 'POST',
        body: {
          // trackSkipIDs: rows.map(({ track: { uri = '' } = {} }) =>
          //   uri.substr(-22)
          // ),
          trackIDs: rows.map((row) => row?.track?.uri?.substr(-22)),
          numResults: 20,
          // numResults: 3,
          playlistURI: playlistId && `spotify:playlist:${playlistId}`,
          type: 'KPE',
          title,
        },
      }
    ).then(({ recommended_tracks: tracks }) =>
      store.setState(
        produce(store.getState(), (draft) => {
          if (!draft.assistedCurationByPlaylistId[playlistId])
            draft.assistedCurationByPlaylistId[playlistId] = {};
          draft.assistedCurationByPlaylistId[playlistId].tracks = tracks;
        })
      )
    );

    const host = getHost();
    const seedsUrlObject = new URL(`${host}/p2-playground/v1/seeds?limit=20`);

    if (!host.startsWith('https://playlist-playground.spotify'))
      seedsUrlObject.searchParams.set('username', store.getState().user.id);

    if (playlist?.name) seedsUrlObject.searchParams.set('title', playlist.name);

    if (rows?.length)
      seedsUrlObject.searchParams.set(
        'tracks',
        rows.map((row) => row?.track?.uri?.substr(-22))
      );

    spfetch(seedsUrlObject.toString()).then(
      ({ seeds: { artists: artistUris = [], genre = [], mood = [] } = {} }) => {
        store.setState(
          produce(store.getState(), (draft) => {
            if (!draft.assistedCurationByPlaylistId[playlistId])
              draft.assistedCurationByPlaylistId[playlistId] = {};
            draft.assistedCurationByPlaylistId[playlistId].genres = genre.map(
              (genre) => ({
                uri: `spotify:genre:${encodeURIComponent(genre)}`,
                name: genre,
              })
            );
            draft.assistedCurationByPlaylistId[playlistId].moods = mood.map(
              (mood) => ({
                uri: `spotify:mood:${encodeURIComponent(mood)}`,
                name: mood,
              })
            );
          })
        );

        spfetch(
          `https://api.spotify.com/v1/artists?ids=${artistUris
            .slice(0, 50)
            .map((uri) => uri.substr(-22))}`
        ).then(({ artists }) => {
          store.setState(
            produce(store.getState(), (draft) => {
              if (!draft.assistedCurationByPlaylistId[playlistId])
                draft.assistedCurationByPlaylistId[playlistId] = {};
              draft.assistedCurationByPlaylistId[playlistId].artists = artists;
            })
          );
        });
      }
    );
  },

  async fetchStationSeeds({ stationsById, user }, stationId) {
    store.setState({ isLoadingSeedOptions: true });
    return spfetch(
      'https://yahproxy-gew1.spotify.net/v2/iron-chef-recipe-service/v1/recipe-options',
      {
        headers: {
          'x-userid': user?.id,
        },
      }
    ).then(({ artists, genres, decades }) => {
      spfetch('https://api.spotify.com/v1/me/tracks?limit=50').then(
        ({ items = [] }) => {
          const formattedItems = items.map((row) => ({
            ...row,
            uri: row?.track?.uri,
            name: row?.track?.name,
            primary_artist_name: row?.track?.artists?.[0]?.name,
            primary_artist_uri: row?.track?.artists?.[0]?.uri,
            image: getImagesUrlBySize(row?.track?.album?.images, 48),
          }));

          store.setState(
            produce(store.getState(), (draft) => {
              draft.stationSeedsByStationId[stationId].liked_songs = {
                name: 'Liked songs',
                sections: [
                  {
                    items: formattedItems,
                  },
                ],
              };
            })
          );
        }
      );

      spfetch('//api.spotify.com/v1/me/player/recently-played?limit=50').then(
        ({ items = [] }) => {
          const itemUriSet = new Set();
          const formattedItems = items
            .map((row) => ({
              ...row,
              uri: row?.track?.uri,
              name: row?.track?.name,
              primary_artist_name: row?.track?.artists?.[0]?.name,
              primary_artist_uri: row?.track?.artists?.[0]?.uri,
              image: getImagesUrlBySize(row?.track?.album?.images, 48),
            }))
            .filter((row) =>
              itemUriSet.has(row.uri) ? false : itemUriSet.add(row.uri)
            );

          store.setState(
            produce(store.getState(), (draft) => {
              draft.stationSeedsByStationId[stationId].recently_played = {
                name: 'Recently Played',
                sections: [
                  {
                    items: formattedItems,
                  },
                ],
              };
            })
          );
        }
      );

      return produce(store.getState(), (draft) => {
        draft.hasLoadedSeedOptions = true;
        draft.isLoadingSeedOptions = false;

        const formattedResponse = {
          artist: {
            name: 'Artists',
            sections: [
              {
                name: '',
                items: artists.artistClusters
                  .map(({ artistItems }) =>
                    artistItems.map(({ displayName, fileID, uri }) => ({
                      uri: uri,
                      name: displayName,
                      image: fileID && `https://i.scdn.co/image/${fileID}`,
                    }))
                  )
                  .flat(),
              },
            ],
          },
          genre: {
            name: 'Genres',
            sections: [
              {
                name: '',
                items: genres.map(({ name, displayName }) => ({
                  name: displayName,
                  uri: `spotify:genre:${name}`,
                })),
              },
            ],
          },
          decade: {
            name: 'Decades',
            sections: [
              {
                name: '',
                items: decades.map(({ name, displayName }) => ({
                  name: displayName,
                  uri: `spotify:decade:${name}`,
                })),
              },
            ],
          },
        };

        draft.stationSeedsByStationId[stationId] = formattedResponse;

        for (let [type, sections] of Object.entries(formattedResponse)) {
          sections.sections
            .map((section) => section?.items)
            .flat()
            .concat(sections.all || [])
            .forEach((item) => {
              draft[`${type}sByUri`][item?.uri] =
                getTypeFromUri(item?.uri) === 'track'
                  ? {
                      track: {
                        name: item?.name,
                        album: { images: [{ url: item?.image }] },
                        artists: [
                          {
                            uri: item?.primary_artist_uri,
                            name: item?.primary_artist_name,
                          },
                        ],
                      },
                      ...item,
                    }
                  : item;
            });
        }

        formattedResponse.suggested = formattedResponse.track;
      });
    });
  },

  saveSearchItem({ searchItems }, newSearchItemCandidate) {
    const newSearchItemCandidateUri = getHubItemUri(newSearchItemCandidate);
    if (
      newSearchItemCandidateUri &&
      !searchItems.find(
        (searchItem) => getHubItemUri(searchItem) === newSearchItemCandidateUri
      )
    ) {
      return produce(store.getState(), (draft) => {
        draft.searchItems.unshift(newSearchItemCandidate);
      });
    }
  },

  removeSearchItem({ searchItems }, removeSearchItemCandidate) {
    const removeSearchItemCandidateUri = getHubItemUri(
      removeSearchItemCandidate
    );
    const removeSearchItemIndex = searchItems.findIndex(
      (searchItem) => getHubItemUri(searchItem) === removeSearchItemCandidateUri
    );

    if (removeSearchItemIndex !== -1) {
      return produce(store.getState(), (draft) => {
        draft.searchItems.splice(removeSearchItemIndex, 1);
      });
    }
  },

  clearSearchItems({ searchItems }) {
    return {
      searchItems: [],
    };
  },

  async toggleStationSeedsItemUri(
    state,
    stationId,
    itemUri,
    isAdded,
    isSearching,
    searchString,
    section
  ) {
    const type = getTypeFromUri(itemUri);
    section = section || type;
    if (isAdded && type) {
      spplplfetch(
        `/p2-playground/v1/station/similar-items?seed=${itemUri}`
      ).then(({ code, status, similar_items: similarItems }) => {
        if (code === -1 && status) console.error(status);

        if (!similarItems?.length) return;

        if (isSearching) {
          const currentStationSearchItems = store.getState()
            .stationSearchItemsByTypeAndSearchString?.[type]?.[searchString];

          const allCurrentItemUris =
            currentStationSearchItems
              ?.map((item) => item?.uri)
              ?.filter(Boolean) || [];

          let indexOfAddedItemRecommendation;

          indexOfAddedItemRecommendation = currentStationSearchItems?.findIndex(
            (item) => item?.uri === itemUri
          );

          if (indexOfAddedItemRecommendation === -1) return;

          const filteredRecommendedItems = similarItems
            ?.filter(
              (similarItem) => !allCurrentItemUris.includes(similarItem?.uri)
            )
            ?.slice(0, 3);

          const state = store.getState();
          if (
            !filteredRecommendedItems?.length ||
            !state.stationSearchItemsByTypeAndSearchString[type]?.[searchString]
          )
            return;

          store.setState(
            produce(state, (draft) => {
              draft.stationSearchItemsByTypeAndSearchString[type][
                searchString
              ].splice(
                indexOfAddedItemRecommendation + 1,
                0,
                ...filteredRecommendedItems
              );

              filteredRecommendedItems.forEach(
                (item) => (draft[`${type}sByUri`][item?.uri] = item)
              );
            })
          );
        } else {
          const currentStationSeedsSections = store.getState()
            .stationSeedsByStationId[stationId]?.[section]?.sections;

          const allCurrentItemUris = currentStationSeedsSections
            ?.map((section) => section?.items)
            .flat()
            .map((item) => item?.uri)
            .filter(Boolean);

          let indexOfAddedItemRecommendationSection;
          let indexOfAddedItemRecommendationInAboveSection;

          indexOfAddedItemRecommendationSection = currentStationSeedsSections?.findIndex(
            (section, i) => {
              const candidateIndexOfAddedItemRecommendationInAboveSection = section?.items?.findIndex(
                (item) => item?.uri === itemUri
              );
              if (
                candidateIndexOfAddedItemRecommendationInAboveSection !== -1 &&
                typeof candidateIndexOfAddedItemRecommendationInAboveSection ===
                  'number'
              ) {
                indexOfAddedItemRecommendationInAboveSection = candidateIndexOfAddedItemRecommendationInAboveSection;
                return true;
              }
            }
          );

          if (indexOfAddedItemRecommendationInAboveSection === -1) return;

          const filteredRecommendedItems = similarItems
            ?.filter(
              (similarItem) => !allCurrentItemUris.includes(similarItem?.uri)
            )
            ?.slice(0, 3);

          if (!filteredRecommendedItems?.length) return;

          store.setState(
            produce(store.getState(), (draft) => {
              draft.stationSeedsByStationId[stationId][section].sections[
                indexOfAddedItemRecommendationSection
              ].items.splice(
                indexOfAddedItemRecommendationInAboveSection + 1,
                0,
                ...filteredRecommendedItems
              );

              filteredRecommendedItems.forEach(
                (item) =>
                  (draft[`${type}sByUri`][item?.uri] =
                    getTypeFromUri(item?.uri) === 'track'
                      ? {
                          track: {
                            name: item?.name,
                            album: { images: [{ url: item?.image }] },
                            artists: [
                              {
                                uri: item?.primary_artist_uri,
                                name: item?.primary_artist_name,
                              },
                            ],
                          },
                          ...item,
                        }
                      : item)
              );
            })
          );
        }
      });
    }
  },

  async searchStationItems(state, type, searchString) {
    // stationSearchItemsByTypeAndSearchString
    switch (type) {
      case 'track':
      case 'artist':
      case 'playlist':
        return spfetch(
          `https://api.spotify.com/v1/search?q=${encodeURIComponent(
            searchString
          )}&type=${type}`
        ).then(({ [`${type}s`]: { items } }) =>
          produce(store.getState(), (draft) => {
            items.forEach((item) => {
              item.image = getImagesUrlBySize(
                item?.images || item?.album?.images,
                104
              );
              draft[`${type}sByUri`][item?.uri] = item;
            });
            draft.stationSearchItemsByTypeAndSearchString[type][
              searchString
            ] = items;
          })
        );
        break;
    }
  },

  async search({ searchesBySectionAndString }, searchString, section) {
    if (
      section in searchesBySectionAndString &&
      searchString in searchesBySectionAndString[section]
    )
      return;

    return spfetch(
      `https://gew-spclient.spotify.com/searchview/android/v4/search/${
        section ? `${section}/` : ''
      }${encodeURIComponent(
        searchString
      )}*?locale=en&country=SE&catalogue=premium&limit=4&entityVersion=5`
    ).then((response) =>
      produce(store.getState(), (draft) => {
        if (!(section in draft.searchesBySectionAndString))
          draft.searchesBySectionAndString[section] = {};
        draft.searchesBySectionAndString[section][searchString] = response;
      })
    );
  },

  async editOrCreateDynamicPlaylist(state, stationId, name, selected, history) {
    const isEditing = !!stationId;
    const onlyTracks =
      Array.from(selected).filter((uri) => getTypeFromUri(uri) === 'track')
        .length === selected.size;

    if (onlyTracks) {
      stationId = stationId || Date.now().toString().padStart(32, '0');
      history.replace(`/playlist/${stationId}`);

      return produce(state, (draft) => {
        const playlist =
          draft.playlistsById[stationId] ||
          (draft.playlistsById[stationId] = {
            id: stationId,
            ownerDisplayName: draft?.user?.display_name,
            ownerImageUrl: draft?.user?.images?.[0]?.url,
            isOwnedByCurrentUser: true,
            rowsBySteering: {},
          });

        playlist.name = name;
        const rows = (playlist.rows = playlist.rowsBySteering[
          'undefined'
        ] = Array.from(selected)
          .map((uri) => draft.tracksByUri[uri])
          .filter(Boolean));

        const uniqueTrackAlbumImageUrlsSet = new Set(
          rows.map((row) => row?.track?.album?.images?.[0]?.url).filter(Boolean)
        );

        playlist.imageUrl =
          uniqueTrackAlbumImageUrlsSet.size >= 4
            ? `https://mosaic.scdn.co/300/${Array.from(
                uniqueTrackAlbumImageUrlsSet
              )
                .slice(0, 4)
                .map((uniqueTrackAlbumImageUrl) =>
                  uniqueTrackAlbumImageUrl.substr(-40)
                )
                .join('')}`
            : Array.from(uniqueTrackAlbumImageUrlsSet)?.[0] || null;
      });
    }

    const { playlist_id: newPlaylistId } = await spfetch(
      `https://yahproxy-gew1.spotify.net/v2/iron-chef-recipe-service/v1/recipe${
        stationId ? `/${stationId}` : ''
      }`,
      {
        headers: {
          'x-userid': state.user?.id,
        },
        body: {
          user_id: state.user?.id,
          name,
          artist_uris: Array.from(selected).filter(
            (uri) => getTypeFromUri(uri) === 'artist'
          ),
          genres: Array.from(selected)
            .filter((uri) => getTypeFromUri(uri) === 'genre')
            .map((uri) => uri.match(/[^:]+$/)?.[0]),
          decades: Array.from(selected)
            .filter((uri) => getTypeFromUri(uri) === 'decade')
            .map((uri) => uri.match(/[^:]+$/)?.[0]),
        },
        method: stationId ? 'PUT' : 'POST',
      }
    );

    if (isEditing) this.fetchDynamicPlaylist(stationId, true, selected);

    history.replace(`/dynamic-playlist/${newPlaylistId || stationId}`);
  },

  async fetchDynamicPlaylist(state, stationId, forceRefresh, selected) {
    if (!state.hasLoadedSeedOptions && !state.isLoadingSeedOptions)
      await this.fetchStationSeeds(stationId);

    await state.user;

    store.setState(
      produce(store.getState(), (draft) => {
        if (!(stationId in draft.playlistsById))
          draft.playlistsById[stationId] = {};

        const playlist = draft.playlistsById[stationId];
        playlist.isOwnedByCurrentUser = true;
        playlist.isOwnedBySpotify = false;
        playlist.ownerDisplayName = draft.user?.display_name;
        playlist.ownerImageUrl = draft.user.images?.[0]?.url;
      })
    );

    await this.fetchPlaylist(stationId, forceRefresh);

    return spfetch(
      `https://yahproxy-gew1.spotify.net/v2/iron-chef-recipe-service/v1/recipe/${stationId}`
    ).then(async ({ artist_uris, decades, genres }) => {
      let newlyDecoratedArtists = [];
      if (artist_uris?.length) {
        const artistsByUri = store.getState().artistsByUri;
        const artistUrisToDecorate = artist_uris.filter(
          (artistUri) => !(artistUri in artistsByUri)
        );
        if (artistUrisToDecorate.length) {
          newlyDecoratedArtists = await spfetch(
            `https://api.spotify.com/v1/artists?ids=${artistUrisToDecorate
              .map((artistUri) => artistUri.substr(-22))
              .join(',')}`
          ).then(({ artists }) => artists);
        }
      }

      return produce(store.getState(), (draft) => {
        newlyDecoratedArtists.forEach((artist) => {
          artist.image = getImagesUrlBySize(artist?.images, 104);
          draft.artistsByUri[artist.uri] = artist;
        });

        if (!(stationId in draft.playlistsById))
          draft.playlistsById[stationId] = {};
        const playlist = draft.playlistsById[stationId];

        playlist.seeds = [
          ...artist_uris?.map((artistUri) => draft.artistsByUri[artistUri]),
          ...genres?.map(
            (genre) => draft.genresByUri[`spotify:genre:${genre}`]
          ),
          ...decades?.map(
            (decade) => draft.decadesByUri[`spotify:decade:${decade}`]
          ),
        ];
      });
    });
  },

  async fetchMoreDynamicPlaylist(state, stationId) {
    if (state.playlistsById[stationId]?.isLoadingMore) return;

    store.setState(
      produce(state, (draft) => {
        draft.playlistsById[stationId].isLoadingMore = true;
      })
    );

    const MORE_DESIRED_TRACK_COUNT = 15;
    let rows = store.getState().playlistsById[stationId]?.rows?.concat() || [];
    const TARGET_DESIRED_TRACK_COUNT = rows.length + MORE_DESIRED_TRACK_COUNT;

    while (rows?.length < TARGET_DESIRED_TRACK_COUNT) {
      const { next_tracks: tracks = [] } = await spplplfetch(
        `/p2-playground/v1/station/next-tracks?station-id=${encodeURIComponent(
          stationId
        )}&limit=${MORE_DESIRED_TRACK_COUNT}`
      );

      if (!tracks.length) break;

      const addedRows =
        tracks?.map(
          ({
            album_image_large,
            album_image_small,
            album_name,
            album_uri,
            name,
            primary_artist_name,
            primary_artist_uri,
            uri,
            source,
          }) => ({
            uid: uri.substr(-22),
            metadata: { source },
            track: {
              uri,
              name,
              album: {
                uri: album_uri,
                name: album_name,
                images: [album_image_large, album_image_small].map((url) => ({
                  url,
                })),
              },
              artists: [
                {
                  uri: primary_artist_uri,
                  name: primary_artist_name,
                },
              ],
            },
          })
        ) || [];

      rows = rows.concat(addedRows);
    }

    // FIXME: update queue if currently playing
    store.setState(
      produce(store.getState(), (draft) => {
        const uniqueTrackAlbumImageUrlsSet = new Set(
          rows.map((row) => row?.track?.album?.images?.[0]?.url)
        );

        draft.playlistsById[stationId].isLoadingMore = false;
        draft.playlistsById[stationId].rows = rows;
        draft.playlistsById[stationId].rowsBySteering['undefined'] = rows;

        draft.playlistsById[stationId].imageUrl =
          uniqueTrackAlbumImageUrlsSet.size >= 4
            ? `https://mosaic.scdn.co/300/${Array.from(
                uniqueTrackAlbumImageUrlsSet
              )
                .slice(0, 4)
                .map((uniqueTrackAlbumImageUrl) =>
                  uniqueTrackAlbumImageUrl.substr(-40)
                )
                .join('')}`
            : Array.from(uniqueTrackAlbumImageUrlsSet)?.[0] || null;

        rows.forEach(
          (row) => (draft.tracksByUri[row?.track?.uri] = row?.track)
        );
      })
    );
  },

  async addTrackToPlaylist({ playlistsById }, playlistId, trackObject) {
    store.setState({
      playlistsById: {
        ...playlistsById,
        [playlistId]: {
          ...playlistsById[playlistId],
          rows: playlistsById[playlistId].rows.concat(trackObject),
        },
      },
    });

    const {
      ok,
    } = await spfetch(
      `https://api.spotify.com/v1/playlists/${playlistId}/tracks?uris=${encodeURIComponent(
        trackObject.track.uri
      )}`,
      { method: 'POST' }
    );

    if (ok) {
      this.fetchPlaylist(playlistId);
    } else
      store.setState({
        playlistsById: {
          ...playlistsById,
          [playlistId]: {
            ...playlistsById[playlistId],
            rows: playlistsById[playlistId].rows.filter(
              (existingTrackObject) => existingTrackObject !== trackObject
            ),
          },
        },
      });
  },

  async preloadTrackPreview(
    { previewFileUriByTrackUri, preview: a },
    trackUri
  ) {
    if (!trackUri) return;
    if (trackUri in previewFileUriByTrackUri)
      return await previewFileUriByTrackUri[trackUri];

    const promise = spfetch(
      `https://spclient.wg.spotify.com/metadata/4/track/${base62Tohex(
        trackUri.substr(-22)
      )}?alt=json`
    );

    const {
      alternative: [{ preview: alternativePreview } = {}] = [],
      preview = alternativePreview,
    } = await promise;

    const mp3PreviewId = (
      (preview || []).find(({ format = '' }) => format.startsWith('MP3')) || {}
    ).file_id;

    const previewFileUri =
      mp3PreviewId && `https://p.scdn.co/mp3-preview/${mp3PreviewId}`;

    return produce(store.getState(), (draft) => {
      draft.previewFileUriByTrackUri[trackUri] = previewFileUri;
    });
  },

  async playPreview(
    {
      preview: { uri: currentPreviewUri, fileUri: currentFileUri } = {},
      playerState: { paused } = {},
    },
    uri,
    event
  ) {
    const isPlaying = paused === false;
    const shouldResumePlaybackWhenDonePreviewing = false;

    if (isPlaying) this.togglePlayback();

    if (currentPreviewUri === uri && currentFileUri) {
      return {
        preview: {
          ...store.getState().preview,
          isPlaying: true,
          shouldResumePlaybackWhenDonePreviewing,
          key: Date.now(),
        },
      };
    } else {
      const key = Date.now();
      store.setState({
        preview: {
          uri,
          fileUri:
            'data:audio/mpeg;base64,/+MYxAAAAANIAAAAAExBTUUzLjk4LjIAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA',
          isMuted: true,
          isPlaying: true,
          shouldResumePlaybackWhenDonePreviewing,
          key,
        },
      });

      await this.preloadTrackPreview(uri);

      const fileUri = store.getState().previewFileUriByTrackUri[uri];

      return {
        preview: {
          uri,
          fileUri,
          isPlaying: !!fileUri,
          isMuted: !fileUri,
          shouldResumePlaybackWhenDonePreviewing,
          key,
        },
      };
    }
  },

  pausePreview({ preview, playerState: { paused } = {} }, uri) {
    if (preview.shouldResumePlaybackWhenDonePreviewing && paused)
      this.togglePlayback();
    return {
      preview: {
        ...preview,
        isPlaying: false,
        shouldResumePlaybackWhenDonePreviewing: false,
      },
    };
  },

  openContextMenu(state, contextMenuUri, contextMenuContextUri) {
    return {
      contextMenuUri,
      contextMenuContextUri,
      isContextMenuOpen: true,
    };
  },

  toggleNowPlayingView({ isNowPlayingViewOpen }) {
    return {
      isNowPlayingViewOpen: !isNowPlayingViewOpen,
    };
  },

  closeNowPlayingView() {
    return {
      isNowPlayingViewOpen: false,
    };
  },

  closeContextMenu() {
    return {
      isContextMenuOpen: false,
    };
  },

  showNotification(state, text, id, button) {
    return {
      notification: {
        text,
        id,
        button,
      },
    };
  },

  toggleShuffle({ isShuffleEnabled }) {
    store.setState({
      isShuffleEnabled: !isShuffleEnabled,
    });
  },

  async createNewPlaylist(
    { user: { id: currentUserId } = {} },
    history,
    name,
    isAutoRefreshEnabled
  ) {
    const {
      id: playlistId,
      resultingName,
      external_urls: { spotify: url } = {},
    } = await spfetch(
      `https://api.spotify.com/v1/users/${currentUserId}/playlists`,
      {
        method: 'POST',
        body: {
          name: name || 'My playlist #1',
        },
      }
    );

    store.setState(
      produce(store.getState(), (draft) => {
        draft.playlistsById[playlistId] = { isAutoRefreshEnabled, name };
      })
    );

    history.push(`/playlist/${playlistId}/new`);
  },

  async createNewPlaylistWithTracks(
    { user: { id: currentUserId } = {} },
    history,
    name,
    rows
  ) {
    const {
      id: playlistId,
      resultingName,
      external_urls: { spotify: url } = {},
    } = await spfetch(
      `https://api.spotify.com/v1/users/${currentUserId}/playlists`,
      {
        method: 'POST',
        body: {
          name: name || 'My playlist #1',
        },
      }
    );

    store.setState(
      produce(store.getState(), (draft) => {
        draft.playlistsById[playlistId] = { name, rows };
      })
    );

    history.push(`/playlist/${playlistId}`);

    const {
      ok,
    } = await spfetch(
      `https://api.spotify.com/v1/playlists/${playlistId}/tracks?uris=${encodeURIComponent(
        rows.map((row) => row?.track?.uri).join(',')
      )}`,
      { method: 'POST' }
    );

    if (ok) this.fetchPlaylist(playlistId, true);
  },

  setVolume(
    {
      descriptor: { id: currentDeviceId = 'unknown' } = {},
      device: { id: activeDeviceId = currentDeviceId } = {},
    },
    volume
  ) {
    spfetch(
      `https://spclient.wg.spotify.com/connect-state/v1/connect/volume/from/${currentDeviceId}/to/${
        activeDeviceId || currentDeviceId
      }`,
      {
        method: 'PUT',
        body: {
          volume,
        },
      }
    );
  },

  addSeedRecommendationToPlaylist(state, playlistId, uri) {
    return produce(state, (draft) => {
      const playlist = draft.playlistsById[playlistId];
      const rows = playlist.rowsBySteering[playlist.steer] || playlist.rows;
      const row = rows?.find((row) => row?.track?.uri === uri);
      if (row && row?.metadata?.source !== 'track-seeds') {
        if (!row.metadta) row.metadata = {};
        row.metadata.source = 'track-seeds';

        spplplfetch(
          `/p2-playground/v1/station/add-seeds?station-id=${playlistId}&seeds=${uri}`
        ).then(() =>
          spplplfetch(
            `/p2-playground/v1/station/reset?station-id=${playlistId}`
          )
        );
      }
    });
  },

  removeUriFromPlaylistClientside(state, playlistId, uri) {
    return produce(state, (draft) => {
      const playlist = draft.playlistsById[playlistId];
      if (!playlist) return;

      const rows = playlist.rowsBySteering[playlist.steer];
      const indexToRemove = rows.findIndex((row) => row?.track?.uri === uri);
      if (indexToRemove === -1) return;
      rows.splice(indexToRemove, 1);
    });
  },
});

export default actions;

const site = new URLSearchParams(global.location.search.slice(1)).get('site');
function getHost() {
  switch (site) {
    case 'guc':
    case 'us':
      return 'http://guc3-playlistplayground-a-0wl7.guc3.spotify.net';
    case 'gew':
    case 'eu':
      return 'http://gew1-playlistplayground-a-sm88.gew1.spotify.net';
    case 'internal-edge':
      return 'https://playlist-playground.spotify.net/edge';
    case 'external-edge':
      return 'https://playlist-playground.spotify.com/edge';
    case 'lex':
      return 'https://lexlabs.spotify.net';
    default:
      return 'https://playlist-playground.spotify.com/edge';
    // return 'https://lexlabs.spotify.net';
  }
}

const getPlPlUrlObject = (endpoint) => {
  const host = getHost();
  const urlObject = new URL(`${host}${endpoint}`);
  if (
    !host.startsWith('https://playlist-playground.spotify') &&
    !urlObject.searchParams.get('username')
  )
    urlObject.searchParams.set(
      'username',
      'litenjacob' || store.getState().user.id
    );
  return urlObject;
};

export function spplplfetch(endpoint, input) {
  const urlObject = getPlPlUrlObject(endpoint);
  return spfetch(urlObject.toString(), input);
}

global.spplplfetch = spplplfetch;

const getStationUriFromStationId = (stationId) =>
  `spotify:internal:station:${stationId}`;

const formatTime = (time) =>
  time == null
    ? '-:--'
    : `${Math.floor(time / 1000 / 60)}:${Math.round((time / 1000) % 60)
        .toString()
        .padStart(2, '0')}`;
